/*
 * WCAG 2.2.2 (Pause, Stop, Hide) + 2.3.3 (Animation from Interactions)
 *
 * Floating pause/play toggle that:
 *   • Reads "[⏸ ANIMATION]" or "[▶ ANIMATION]" depending on state
 *   • Pauses every autoplaying Swiper + autoplay <video> on the page
 *   • Persists the user's choice in localStorage
 *   • Respects OS-level prefers-reduced-motion
 *   • Only appears when the footer enters the viewport
 *   • On PDPs, lifts above the sticky Add-To-Cart bar when that's visible
 *   • Tab key always brings it into view (WCAG 2.2.2 reachability)
 */
(function () {
    "use strict";

    var STORAGE_KEY = "tabs.a11y.motionPaused";

    function reducedMotionMedia() {
        return window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
    }

    function storedChoice() {
        try {
            var v = localStorage.getItem(STORAGE_KEY);
            return v === "0" || v === "1" ? v : null;
        } catch (e) { return null; }
    }

    function storePaused(paused) {
        try { localStorage.setItem(STORAGE_KEY, paused ? "1" : "0"); } catch (e) {}
    }

    function applyPauseToAll(paused) {
        document.querySelectorAll(".swiper-initialized").forEach(function (el) {
            var s = el.swiper;
            if (!s || !s.autoplay) return;
            if (paused) s.autoplay.stop();
            else if (s.params && s.params.autoplay) s.autoplay.start();
        });
        document.querySelectorAll("video[autoplay]").forEach(function (v) {
            if (paused) {
                try { v.pause(); v.dataset.a11yMotionPaused = "1"; } catch (e) {}
            } else if (v.dataset.a11yMotionPaused === "1") {
                try {
                    var p = v.play();
                    if (p && typeof p.catch === "function") p.catch(function () {});
                } catch (e) {}
                delete v.dataset.a11yMotionPaused;
            }
        });
    }

    function createToggleButton() {
        if (document.getElementById("a11yMotionToggle")) return;
        var btn = document.createElement("button");
        btn.id = "a11yMotionToggle";
        btn.type = "button";
        btn.setAttribute("aria-pressed", "false");
        btn.setAttribute("aria-label", "Pause animations and autoplaying carousels");
        // Start in .isHidden — the footer-visibility logic will reveal it.
        btn.className = "a11yMotionToggle isHidden";
        btn.innerHTML =
            '<span class="a11yMotionTogglePauseIcon" aria-hidden="true">' +
                '<svg width="14" height="14" viewBox="0 0 14 14" focusable="false"><rect x="3" y="2" width="3" height="10" fill="currentColor"/><rect x="8" y="2" width="3" height="10" fill="currentColor"/></svg>' +
            '</span>' +
            '<span class="a11yMotionTogglePlayIcon" aria-hidden="true">' +
                '<svg width="14" height="14" viewBox="0 0 14 14" focusable="false"><path d="M3 1.5L12 7L3 12.5V1.5Z" fill="currentColor"/></svg>' +
            '</span>' +
            '<span class="a11yMotionToggleText">Animation</span>';

        btn.addEventListener("click", function () {
            var nextPaused = btn.getAttribute("aria-pressed") !== "true";
            setPaused(nextPaused, true);
        });
        document.body.appendChild(btn);
        return btn;
    }

    function setPaused(paused, userInitiated) {
        if (userInitiated) storePaused(paused);
        var btn = document.getElementById("a11yMotionToggle");
        if (btn) {
            btn.setAttribute("aria-pressed", paused ? "true" : "false");
            btn.setAttribute(
                "aria-label",
                paused ? "Play animations and autoplaying carousels" : "Pause animations and autoplaying carousels"
            );
            btn.classList.toggle("isPaused", paused);
        }
        document.documentElement.toggleAttribute("data-a11y-motion-paused", paused);
        var explicitPlay = !paused && (userInitiated || storedChoice() === "0");
        document.documentElement.toggleAttribute("data-a11y-motion-play", explicitPlay);
        applyPauseToAll(paused);
    }

    function init() {
        createToggleButton();

        // Decide initial pause state: explicit user choice > system preference
        var rm = reducedMotionMedia();
        var choice = storedChoice();
        var initial = choice !== null ? choice === "1" : !!(rm && rm.matches);
        setPaused(initial, false);

        // Re-apply pause state when new Swipers / videos mount
        new MutationObserver(function () {
            if (document.documentElement.hasAttribute("data-a11y-motion-paused")) {
                applyPauseToAll(true);
            }
        }).observe(document.body, { childList: true, subtree: true });

        // React to OS-level prefers-reduced-motion changes mid-session
        if (rm && rm.addEventListener) {
            rm.addEventListener("change", function (e) {
                if (storedChoice() === null) setPaused(e.matches, false);
            });
        }

        /* ============================================================
           VISIBILITY: toggle is hidden by default and only appears when
           the page footer is in viewport. Scroll-idle hides during active
           scroll; restores 800ms after scroll stops (if footer still visible).
           ============================================================ */
        var footerVisible = false;
        var scrollIdleTimer = null;
        var SCROLL_IDLE_MS = 800;

        function updateVisibility() {
            var b = document.getElementById("a11yMotionToggle");
            if (!b) return;
            b.classList.toggle("isHidden", !footerVisible);
        }

        function watchFooter() {
            var footerEl = document.getElementById("footer") || document.querySelector("footer");
            if (!footerEl || !("IntersectionObserver" in window)) return false;
            new IntersectionObserver(function (entries) {
                entries.forEach(function (e) { footerVisible = e.isIntersecting; });
                updateVisibility();
            }, { threshold: 0 }).observe(footerEl);
            return true;
        }
        if (!watchFooter()) {
            var fRetries = 0;
            var fPoll = setInterval(function () {
                fRetries++;
                if (watchFooter() || fRetries > 20) clearInterval(fPoll);
            }, 250);
        }

        /* ============================================================
           STICKY ATC CLEARANCE: when the PDP's .productATCFixed bar is
           in viewport, lift the toggle above it so they don't overlap.
           ============================================================ */
        function watchStickyATC() {
            var atc = document.querySelector(".productATCFixed");
            if (!atc || !("IntersectionObserver" in window)) return false;
            new IntersectionObserver(function (entries) {
                var b = document.getElementById("a11yMotionToggle");
                if (!b) return;
                entries.forEach(function (e) {
                    if (e.isIntersecting) {
                        var h = e.target.offsetHeight || 80;
                        b.style.bottom = (h + 12) + "px";
                    } else {
                        b.style.bottom = "";
                    }
                });
            }, { threshold: 0 }).observe(atc);
            return true;
        }
        if (!watchStickyATC()) {
            var aRetries = 0;
            var aPoll = setInterval(function () {
                aRetries++;
                if (watchStickyATC() || aRetries > 20) clearInterval(aPoll);
            }, 250);
        }

        // Scroll-idle hiding (suppresses the pill while user is actively scrolling)
        window.addEventListener("scroll", function () {
            var b = document.getElementById("a11yMotionToggle");
            if (!b) return;
            b.classList.add("isScrolling");
            if (scrollIdleTimer) clearTimeout(scrollIdleTimer);
            scrollIdleTimer = setTimeout(function () {
                b.classList.remove("isScrolling");
                updateVisibility();
            }, SCROLL_IDLE_MS);
        }, { passive: true });

        updateVisibility();
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init);
    } else {
        init();
    }
})();
